import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { DATABASE } from '../../../../Config/firebase';

const TOLERANCE = 0.01;

function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === '') return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

function hasDiscrepancy(value1, value2, tolerance = TOLERANCE) {
  if (value1 === null || value1 === undefined || value2 === null || value2 === undefined) return false;
  return Math.abs(value1 - value2) > tolerance;
}

function getDiscrepancySeverity(diff) {
  const absDiff = Math.abs(diff);
  if (absDiff < 10) return 'low';
  if (absDiff < 100) return 'medium';
  return 'high';
}

function DiscrepancyChip({ diff }) {
  const severity = getDiscrepancySeverity(diff);
  const color = severity === 'low' ? '#2af598' : severity === 'medium' ? '#ffc107' : '#f52a2d';
  
  return (
    <Chip
      label={formatCurrency(diff)}
      sx={{
        backgroundColor: `${color}20`,
        color: color,
        border: `1px solid ${color}40`,
        fontWeight: '600'
      }}
    />
  );
}

const DataComparisonTool = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  const [monthlyComparisons, setMonthlyComparisons] = useState([]);
  const [clientDiscrepancies, setClientDiscrepancies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');

  const runCrossCheck = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Running cross-check...');
      
      // 1. Load spreadsheet data (main)
      console.log('Loading spreadsheet data...');
      let spreadsheetSnapshot;
      try {
        spreadsheetSnapshot = await getDocs(collection(DATABASE, 'fund_history_main'));
        console.log(`Found ${spreadsheetSnapshot.docs.length} spreadsheet months`);
      } catch (err) {
        console.error('Error loading spreadsheet data:', err);
        throw new Error(`Failed to load spreadsheet data: ${err.message}`);
      }
      
      const spreadsheetData = {};
      spreadsheetSnapshot.docs.forEach(doc => {
        try {
          const data = doc.data();
          if (!data.year || !data.month) {
            console.warn(`Skipping document ${doc.id} - missing year or month`);
            return;
          }
          const key = `${data.year}-${String(data.month).padStart(2, '0')}`;
          spreadsheetData[key] = {
            deposits: data.aggregates?.totalDeposits || 0,
            withdrawals: data.aggregates?.totalWithdrawals || 0,
            performance: data.aggregates?.totalNetPerformance || 0,
            aum: data.aggregates?.totalAUM || 0
          };
        } catch (err) {
          console.error(`Error processing document ${doc.id}:`, err);
        }
      });
      console.log(`Loaded ${Object.keys(spreadsheetData).length} spreadsheet months`);

      // 2. Load ledger data - fetch all and filter in code
      console.log('Loading ledger data...');
      let ledgerSnapshot;
      try {
        // Fetch all documents and filter in code (avoids index issues)
        ledgerSnapshot = await getDocs(collection(DATABASE, 'database_calculated_data'));
        console.log(`Found ${ledgerSnapshot.docs.length} total database_calculated_data documents`);
      } catch (err) {
        console.error('Error loading ledger data:', err);
        throw new Error(`Failed to load ledger data: ${err.message}`);
      }
      
      const ledgerData = {};
      let ledgerMonths = 0;
      ledgerSnapshot.docs.forEach(doc => {
        try {
          const data = doc.data();
          // Filter for monthly ledger data (not client data)
          if (data.source === 'ledgers' && data.type !== 'client' && data.year && data.month) {
            const key = `${data.year}-${String(data.month).padStart(2, '0')}`;
            ledgerData[key] = {
              deposits: data.deposits || 0,
              withdrawals: data.withdrawals || 0,
              performance: data.performance || 0,
              aum: data.closingBalance || 0
            };
            ledgerMonths++;
          }
        } catch (err) {
          console.error(`Error processing ledger document ${doc.id}:`, err);
        }
      });
      console.log(`Loaded ${ledgerMonths} ledger months`);

      // 3. Load transaction data - reuse the same snapshot
      console.log('Loading transaction data...');
      const transactionData = {};
      let transactionMonths = 0;
      ledgerSnapshot.docs.forEach(doc => {
        try {
          const data = doc.data();
          // Filter for monthly transaction data (not client data)
          if (data.source === 'transactions' && data.type !== 'client' && data.year && data.month) {
            const key = `${data.year}-${String(data.month).padStart(2, '0')}`;
            transactionData[key] = {
              deposits: data.deposits || 0,
              withdrawals: data.withdrawals || 0,
              performance: data.performance || 0
            };
            transactionMonths++;
          }
        } catch (err) {
          console.error(`Error processing transaction document ${doc.id}:`, err);
        }
      });
      console.log(`Loaded ${transactionMonths} transaction months`);

      // 4. Compare monthly data
      const allMonthKeys = new Set([
        ...Object.keys(spreadsheetData),
        ...Object.keys(ledgerData),
        ...Object.keys(transactionData)
      ]);
      
      console.log(`Total unique months: ${allMonthKeys.size}`);
      console.log(`Spreadsheet months: ${Object.keys(spreadsheetData).length}`);
      console.log(`Ledger months: ${Object.keys(ledgerData).length}`);
      console.log(`Transaction months: ${Object.keys(transactionData).length}`);
      
      const comparisons = [];
      const discrepancies = [];
      
      Array.from(allMonthKeys).sort().forEach(monthKey => {
        const [year, month] = monthKey.split('-').map(Number);
        const spreadsheet = spreadsheetData[monthKey] || {};
        const ledgers = ledgerData[monthKey] || {};
        const transactions = transactionData[monthKey] || {};
        
        const monthDiscrepancies = [];
        
        // Check deposits
        if (spreadsheet.deposits !== undefined && ledgers.deposits !== undefined) {
          if (hasDiscrepancy(spreadsheet.deposits, ledgers.deposits)) {
            monthDiscrepancies.push({
              type: 'deposits',
              spreadsheet: spreadsheet.deposits,
              ledgers: ledgers.deposits,
              diff: spreadsheet.deposits - ledgers.deposits
            });
          }
        }
        
        // Check withdrawals
        if (spreadsheet.withdrawals !== undefined && ledgers.withdrawals !== undefined) {
          if (hasDiscrepancy(spreadsheet.withdrawals, ledgers.withdrawals)) {
            monthDiscrepancies.push({
              type: 'withdrawals',
              spreadsheet: spreadsheet.withdrawals,
              ledgers: ledgers.withdrawals,
              diff: spreadsheet.withdrawals - ledgers.withdrawals
            });
          }
        }
        
        // Check performance
        if (spreadsheet.performance !== undefined && ledgers.performance !== undefined) {
          if (hasDiscrepancy(spreadsheet.performance, ledgers.performance)) {
            monthDiscrepancies.push({
              type: 'performance',
              spreadsheet: spreadsheet.performance,
              ledgers: ledgers.performance,
              diff: spreadsheet.performance - ledgers.performance
            });
          }
        }
        
        comparisons.push({
          year,
          month,
          monthKey,
          sources: {
            mainSpreadsheet: spreadsheet,
            ledgers,
            transactions
          },
          discrepancies: monthDiscrepancies
        });
        
        if (monthDiscrepancies.length > 0) {
          discrepancies.push({ monthKey, year, month, discrepancies: monthDiscrepancies });
        }
      });

      // 5. Load client data - filter from the same snapshot
      console.log('Loading client data...');
      const clientLedgers = {};
      const clientTransactions = {};
      
      ledgerSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.type === 'client' && data.source === 'ledgers' && data.userId) {
          clientLedgers[data.userId] = data;
        } else if (data.type === 'client' && data.source === 'transactions' && data.userId) {
          clientTransactions[data.userId] = data;
        }
      });
      
      console.log(`Found ${Object.keys(clientLedgers).length} client ledger documents`);
      console.log(`Found ${Object.keys(clientTransactions).length} client transaction documents`);
      
      
      // Aggregate spreadsheet data across ALL months (not just last month)
      // This gives us all-time totals like Ledgers/Transactions
      const clientSpreadsheet = {};
      const clientSpreadsheetMap = new Map(); // name -> aggregated data
      
      // Process all months to aggregate deposits/withdrawals/performance
      spreadsheetSnapshot.docs.forEach(doc => {
        const monthData = doc.data();
        if (monthData.clients && Array.isArray(monthData.clients)) {
          monthData.clients.forEach(client => {
            const key = (client.name || '').trim().toLowerCase();
            if (!key) return;
            
            if (!clientSpreadsheetMap.has(key)) {
              clientSpreadsheetMap.set(key, {
                name: client.name,
                email: client.email,
                totalDeposits: 0,
                totalWithdrawals: 0,
                totalPerformance: 0,
                currentBalance: 0 // Will use latest month's closing
              });
            }
            
            const aggregated = clientSpreadsheetMap.get(key);
            aggregated.totalDeposits += client.deposits || 0;
            aggregated.totalWithdrawals += client.withdrawals || 0;
            aggregated.totalPerformance += client.netPerformance || 0;
            // Use latest month's closing balance
            if (monthData.year && monthData.month) {
              const monthKey = `${monthData.year}-${String(monthData.month).padStart(2, '0')}`;
              const lastKey = aggregated.lastMonthKey || '';
              if (!lastKey || monthKey > lastKey) {
                aggregated.currentBalance = client.closingBalance || 0;
                aggregated.lastMonthKey = monthKey;
              }
            }
          });
        }
      });
      
      // Match aggregated spreadsheet data to ledger clients
      Object.keys(clientLedgers).forEach(userId => {
        const ledger = clientLedgers[userId];
        const ledgerName = (ledger.name || '').trim().toLowerCase();
        const ledgerEmail = (ledger.email || '').trim().toLowerCase();
        
        // Try to find matching spreadsheet client
        let matched = null;
        for (const [key, data] of clientSpreadsheetMap.entries()) {
          const dataName = (data.name || '').trim().toLowerCase();
          const dataEmail = (data.email || '').trim().toLowerCase();
          
          if (dataName === ledgerName || 
              dataEmail === ledgerEmail ||
              (ledgerName && dataName && ledgerName.includes(dataName)) ||
              (dataName && ledgerName && dataName.includes(ledgerName))) {
            matched = data;
            break;
          }
        }
        
        if (matched) {
          clientSpreadsheet[userId] = {
            currentBalance: matched.currentBalance,
            totalDeposits: matched.totalDeposits,
            totalWithdrawals: matched.totalWithdrawals,
            totalPerformance: matched.totalPerformance
          };
        }
      });
      
      // 6. Compare client data
      const clientDisc = [];
      Object.keys(clientLedgers).forEach(userId => {
        const ledger = clientLedgers[userId];
        const transaction = clientTransactions[userId];
        const spreadsheet = clientSpreadsheet[userId];
        
        const clientDiscList = [];
        
        if (transaction) {
          if (hasDiscrepancy(ledger.totalDeposits, transaction.totalDeposits)) {
            clientDiscList.push({
              type: 'deposits',
              ledgers: ledger.totalDeposits,
              transactions: transaction.totalDeposits,
              diff: Math.abs(ledger.totalDeposits - transaction.totalDeposits)
            });
          }
          
          if (hasDiscrepancy(ledger.totalWithdrawals, transaction.totalWithdrawals)) {
            clientDiscList.push({
              type: 'withdrawals',
              ledgers: ledger.totalWithdrawals,
              transactions: transaction.totalWithdrawals,
              diff: Math.abs(ledger.totalWithdrawals - transaction.totalWithdrawals)
            });
          }
          
          if (hasDiscrepancy(ledger.totalPerformance, transaction.totalPerformance)) {
            clientDiscList.push({
              type: 'performance',
              ledgers: ledger.totalPerformance,
              transactions: transaction.totalPerformance,
              diff: Math.abs(ledger.totalPerformance - transaction.totalPerformance)
            });
          }
        }
        
        if (spreadsheet) {
          if (hasDiscrepancy(ledger.currentBalance, spreadsheet.currentBalance)) {
            clientDiscList.push({
              type: 'balance',
              ledgers: ledger.currentBalance,
              spreadsheet: spreadsheet.currentBalance,
              diff: Math.abs(ledger.currentBalance - spreadsheet.currentBalance)
            });
          }
        }
        
        if (clientDiscList.length > 0) {
          clientDisc.push({
            userId,
            email: ledger.email,
            name: ledger.name,
            sources: {
              ledgers: {
                currentBalance: ledger.currentBalance,
                totalDeposits: ledger.totalDeposits,
                totalWithdrawals: ledger.totalWithdrawals,
                totalPerformance: ledger.totalPerformance
              },
              transactions: transaction ? {
                totalDeposits: transaction.totalDeposits,
                totalWithdrawals: transaction.totalWithdrawals,
                totalPerformance: transaction.totalPerformance
              } : null,
              spreadsheet: spreadsheet || null
            },
            discrepancies: clientDiscList
          });
        }
      });

      console.log('Comparison results:', {
        totalComparisons: comparisons.length,
        monthsWithDiscrepancies: discrepancies.length,
        sampleMonth: comparisons[0] ? {
          key: comparisons[0].monthKey,
          spreadsheet: comparisons[0].sources.mainSpreadsheet,
          ledgers: comparisons[0].sources.ledgers,
          discrepancies: comparisons[0].discrepancies.length
        } : null
      });
      
      console.log('Client comparison results:', {
        totalClients: Object.keys(clientLedgers).length,
        clientsWithDiscrepancies: clientDisc.length,
        sampleClient: clientDisc[0] ? {
          name: clientDisc[0].name,
          discrepancies: clientDisc[0].discrepancies.length
        } : null
      });
      
      setMonthlyComparisons(comparisons);
      setClientDiscrepancies(
        [...clientDisc].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' }))
      );
      setSelectedClientId('');
      setSummary({
        totalMonths: comparisons.length,
        monthsWithDiscrepancies: discrepancies.length,
        totalClients: Object.keys(clientLedgers).length,
        clientsWithDiscrepancies: clientDisc.length
      });
      
      console.log('Cross-check complete:', {
        months: comparisons.length,
        monthsWithDiscrepancies: discrepancies.length,
        clients: Object.keys(clientLedgers).length,
        clientsWithDiscrepancies: clientDisc.length
      });
      
    } catch (err) {
      console.error('Error running cross-check:', err);
      setError('Error running cross-check: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runCrossCheck();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading && !summary) {
    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
        }}
      >
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#2af598' }} />
          <Typography sx={{ mt: 2, color: '#aaa' }}>Running cross-check...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={runCrossCheck}
            startIcon={<RefreshIcon />}
            sx={{
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
              color: '#fff'
            }}
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#fff', mb: 1 }}>
              Data Comparison Tool
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#ccc' }}>
              Comparison between Spreadsheets, Ledgers and Transactions
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={runCrossCheck}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            sx={{
              borderColor: '#2af598',
              color: '#2af598',
              '&:hover': {
                borderColor: '#2af598',
                backgroundColor: 'rgba(42, 245, 152, 0.1)'
              },
              '&:disabled': {
                borderColor: '#555',
                color: '#555'
              }
            }}
          >
            {loading ? 'Running...' : 'Refresh'}
          </Button>
        </Box>

        {/* Summary */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${summary.totalMonths} Months Analyzed`}
            sx={{
              backgroundColor: 'rgba(42, 245, 152, 0.1)',
              color: '#2af598',
              border: '1px solid rgba(42, 245, 152, 0.3)'
            }}
          />
          <Chip
            icon={<WarningIcon />}
            label={`${summary.monthsWithDiscrepancies} Months with Discrepancies`}
            sx={{
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              color: '#ffc107',
              border: '1px solid rgba(255, 193, 7, 0.3)'
            }}
          />
          <Chip
            icon={<WarningIcon />}
            label={`${summary.clientsWithDiscrepancies} Clients with Discrepancies`}
            sx={{
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              color: '#ffc107',
              border: '1px solid rgba(255, 193, 7, 0.3)'
            }}
          />
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: '#aaa',
                '&.Mui-selected': {
                  color: '#2af598'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#2af598'
              }
            }}
          >
            <Tab label="Monthly Discrepancies" />
            <Tab label="Client Discrepancies" />
            <Tab label="Client Detail (Data Sources)" />
          </Tabs>
        </Box>

        {/* Monthly Discrepancies Tab */}
        {activeTab === 0 && (
          <>
            {!monthlyComparisons || monthlyComparisons.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed rgba(255, 255, 255, 0.2)', borderRadius: '4px' }}>
                <WarningIcon sx={{ fontSize: '48px', color: '#ffc107', mb: 2 }} />
                <Typography sx={{ color: '#aaa', fontSize: '16px', mb: 1 }}>
                  No data found. Please ensure the analysis scripts have been run.
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '14px' }}>
                  Check console for details.
                </Typography>
              </Box>
            ) : monthlyComparisons.filter(month => month.discrepancies && month.discrepancies.length > 0).length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed rgba(255, 255, 255, 0.2)', borderRadius: '4px' }}>
                <CheckCircleIcon sx={{ fontSize: '48px', color: '#2af598', mb: 2 }} />
                <Typography sx={{ color: '#aaa', fontSize: '16px' }}>
                  No monthly discrepancies found. All data matches!
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '14px', mt: 1 }}>
                  {monthlyComparisons.length} months compared
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ backgroundColor: '#1a1a1a', maxHeight: '85vh' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Month
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Deposits (Spreadsheet)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Deposits (Ledgers)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Difference
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Withdrawals (Spreadsheet)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Withdrawals (Ledgers)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Difference
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Performance (Spreadsheet)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Performance (Ledgers)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Difference
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyComparisons
                      .filter(month => month.discrepancies && month.discrepancies.length > 0)
                      .slice(0, 50)
                      .map((month) => {
                        const main = month.sources.mainSpreadsheet || {};
                        const ledgers = month.sources.ledgers || {};
                        
                        const depositsDiff = hasDiscrepancy(main.deposits, ledgers.deposits)
                          ? (main.deposits || 0) - (ledgers.deposits || 0)
                          : null;
                        
                        const withdrawalsDiff = hasDiscrepancy(main.withdrawals, ledgers.withdrawals)
                          ? (main.withdrawals || 0) - (ledgers.withdrawals || 0)
                          : null;
                        
                        const performanceDiff = hasDiscrepancy(main.performance, ledgers.performance)
                          ? (main.performance || 0) - (ledgers.performance || 0)
                          : null;

                        return (
                          <TableRow key={month.monthKey} hover>
                            <TableCell sx={{ color: '#fff' }}>
                              {String(month.month).padStart(2, '0')}/{month.year}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#aaa' }}>
                              {formatCurrency(main.deposits)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#aaa' }}>
                              {formatCurrency(ledgers.deposits)}
                            </TableCell>
                            <TableCell align="right">
                              {depositsDiff !== null ? <DiscrepancyChip diff={depositsDiff} /> : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#aaa' }}>
                              {formatCurrency(main.withdrawals)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#aaa' }}>
                              {formatCurrency(ledgers.withdrawals)}
                            </TableCell>
                            <TableCell align="right">
                              {withdrawalsDiff !== null ? <DiscrepancyChip diff={withdrawalsDiff} /> : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#aaa' }}>
                              {formatCurrency(main.performance)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#aaa' }}>
                              {formatCurrency(ledgers.performance)}
                            </TableCell>
                            <TableCell align="right">
                              {performanceDiff !== null ? <DiscrepancyChip diff={performanceDiff} /> : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* Client Discrepancies Tab */}
        {activeTab === 1 && (
          <>
            {summary && summary.totalClients === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed rgba(255, 255, 255, 0.2)', borderRadius: '4px' }}>
                <WarningIcon sx={{ fontSize: '48px', color: '#ffc107', mb: 2 }} />
                <Typography sx={{ color: '#aaa', fontSize: '16px', mb: 1 }}>
                  No client data found. Please ensure the analysis scripts have been run.
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '14px' }}>
                  Check console for details.
                </Typography>
              </Box>
            ) : (!clientDiscrepancies || clientDiscrepancies.length === 0) ? (
              <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed rgba(255, 255, 255, 0.2)', borderRadius: '4px' }}>
                <CheckCircleIcon sx={{ fontSize: '48px', color: '#2af598', mb: 2 }} />
                <Typography sx={{ color: '#aaa', fontSize: '16px' }}>
                  No client discrepancies found. All data matches!
                </Typography>
                {summary && (
                  <Typography sx={{ color: '#888', fontSize: '14px', mt: 1 }}>
                    {summary.totalClients} clients compared
                  </Typography>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ backgroundColor: '#1a1a1a', maxHeight: '85vh' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Client
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Balance (Ledgers)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Balance (Spreadsheet)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Difference
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Withdrawals (Ledgers)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Withdrawals (Transactions)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Difference
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Performance (Ledgers)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Performance (Transactions)
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: '600', backgroundColor: '#2a2a2a' }}>
                        Difference
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientDiscrepancies.slice(0, 50).map((client) => {
                      const ledgers = client.sources.ledgers || {};
                      const transactions = client.sources.transactions || {};
                      const spreadsheet = client.sources.spreadsheet || {};
                      
                      const balanceDiff = spreadsheet && spreadsheet.currentBalance !== undefined && 
                        hasDiscrepancy(ledgers.currentBalance, spreadsheet.currentBalance)
                          ? ledgers.currentBalance - spreadsheet.currentBalance
                          : null;
                      
                      const withdrawalsDiff = transactions && transactions.totalWithdrawals !== undefined &&
                        hasDiscrepancy(ledgers.totalWithdrawals, transactions.totalWithdrawals)
                          ? ledgers.totalWithdrawals - transactions.totalWithdrawals
                          : null;
                      
                      const performanceDiff = transactions && transactions.totalPerformance !== undefined &&
                        hasDiscrepancy(ledgers.totalPerformance, transactions.totalPerformance)
                          ? ledgers.totalPerformance - transactions.totalPerformance
                          : null;

                      return (
                        <TableRow key={client.userId} hover>
                          <TableCell sx={{ color: '#fff' }}>
                            {client.name}
                            <Typography variant="caption" sx={{ display: 'block', color: '#aaa' }}>
                              {client.email}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#aaa' }}>
                            {formatCurrency(ledgers.currentBalance)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#aaa' }}>
                            {formatCurrency(spreadsheet?.currentBalance)}
                          </TableCell>
                          <TableCell align="right">
                            {balanceDiff !== null ? <DiscrepancyChip diff={balanceDiff} /> : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#aaa' }}>
                            {formatCurrency(ledgers.totalWithdrawals)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#aaa' }}>
                            {formatCurrency(transactions?.totalWithdrawals)}
                          </TableCell>
                          <TableCell align="right">
                            {withdrawalsDiff !== null ? <DiscrepancyChip diff={withdrawalsDiff} /> : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#aaa' }}>
                            {formatCurrency(ledgers.totalPerformance)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#aaa' }}>
                            {formatCurrency(transactions?.totalPerformance)}
                          </TableCell>
                          <TableCell align="right">
                            {performanceDiff !== null ? <DiscrepancyChip diff={performanceDiff} /> : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* Client Detail (Data Sources) Tab */}
        {activeTab === 2 && (
          <Box sx={{ minHeight: '70vh' }}>
            <FormControl fullWidth sx={{ mb: 3, maxWidth: 400 }}>
              <InputLabel id="client-detail-select-label" sx={{ color: '#aaa' }}>
                Select client
              </InputLabel>
              <Select
                labelId="client-detail-select-label"
                value={selectedClientId}
                label="Select client"
                onChange={(e) => setSelectedClientId(e.target.value)}
                sx={{
                  color: '#fff',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2af598' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2af598' }
                }}
              >
                <MenuItem value="">
                  <em>— Select a client —</em>
                </MenuItem>
                {clientDiscrepancies.map((c) => (
                  <MenuItem key={c.userId} value={c.userId}>
                    {c.name} ({c.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedClientId && (() => {
              const client = clientDiscrepancies.find((c) => c.userId === selectedClientId);
              if (!client) return null;
              const ledgers = client.sources.ledgers || {};
              const transactions = client.sources.transactions;
              const spreadsheet = client.sources.spreadsheet;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                    {client.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
                    {client.email}
                  </Typography>

                  <Paper sx={{ p: 3, backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography sx={{ color: '#2af598', fontWeight: '600', mb: 2 }}>
                      Data from Ledgers
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
                      Source: collection <code>database_calculated_data</code>, documents <code>client_ledgers_{client.userId}</code>. Sum of ledger entries (deposits type 0, withdrawals type 1/2, performance type 3).
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell sx={{ color: '#aaa' }}>Current balance</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(ledgers.currentBalance)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ color: '#aaa' }}>Total deposits</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(ledgers.totalDeposits)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ color: '#aaa' }}>Total withdrawals</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(ledgers.totalWithdrawals)}</TableCell></TableRow>
                        <TableRow><TableCell sx={{ color: '#aaa' }}>Total performance</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(ledgers.totalPerformance)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Paper>

                  <Paper sx={{ p: 3, backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography sx={{ color: '#ffc107', fontWeight: '600', mb: 2 }}>
                      Data from Transactions
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
                      Source: collection <code>database_calculated_data</code>, documents <code>client_transactions_{client.userId}</code>. Sum from <code>transection_requests</code> (total_amount for deposits, withdraw_amount for withdrawals, profit_loss for performance).
                    </Typography>
                    {transactions ? (
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell sx={{ color: '#aaa' }}>Total deposits</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(transactions.totalDeposits)}</TableCell></TableRow>
                          <TableRow><TableCell sx={{ color: '#aaa' }}>Total withdrawals</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(transactions.totalWithdrawals)}</TableCell></TableRow>
                          <TableRow><TableCell sx={{ color: '#aaa' }}>Total performance (sum of profit_loss)</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(transactions.totalPerformance)}</TableCell></TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography sx={{ color: '#888' }}>No transaction data for this client.</Typography>
                    )}
                  </Paper>

                  <Paper sx={{ p: 3, backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography sx={{ color: '#aaa', fontWeight: '600', mb: 2 }}>
                      Data from Spreadsheet
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
                      Source: collection <code>fund_history_main</code>, last month document, <code>clients</code> array (matched by name/email). Values from Excel monthly closing.
                    </Typography>
                    {spreadsheet ? (
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell sx={{ color: '#aaa' }}>Closing balance</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(spreadsheet.currentBalance)}</TableCell></TableRow>
                          <TableRow><TableCell sx={{ color: '#aaa' }}>Total deposits</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(spreadsheet.totalDeposits)}</TableCell></TableRow>
                          <TableRow><TableCell sx={{ color: '#aaa' }}>Total withdrawals</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(spreadsheet.totalWithdrawals)}</TableCell></TableRow>
                          <TableRow><TableCell sx={{ color: '#aaa' }}>Net performance</TableCell><TableCell align="right" sx={{ color: '#fff' }}>{formatCurrency(spreadsheet.totalPerformance)}</TableCell></TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography sx={{ color: '#888' }}>No spreadsheet match for this client (name/email).</Typography>
                    )}
                  </Paper>
                </Box>
              );
            })()}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DataComparisonTool;
